import { FormEvent, FunctionComponent, useEffect, useMemo, useState } from "react";
import type { ServiceRequest } from "fhir/r5";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { VALUE_CODES, VALUE_LABELS, ValueCode } from "../../models/OverviewDashboard";
import ActionPlanCreationService, {
    NeedPriority,
    ProposalActionType
} from "../../services/ActionPlanCreationService";
import OrganizationService, { OrganizationNode } from "../../services/OrganizationService";
import { UPCARE_THEMES, UpCareThemeCode } from "../../utils/UpCareThemes";
import styles from "./ActionPlanNewActionMenu.module.css";

export type ActionPlanCreationMode = "need" | "proposal";

interface ActionPlanNewActionMenuProps {
    fhirBaseUrl?: string;
    value?: ValueCode;
    organizationReferences: string[];
    poleOrganization?: {
        reference: string;
        name: string;
    };
    triggerMode?: "menu" | ActionPlanCreationMode;
    triggerLabel?: string;
    onCreated?: (request: ServiceRequest, mode: ActionPlanCreationMode) => void;
    onOpen?: () => void;
}

const ActionPlanNewActionMenu: FunctionComponent<ActionPlanNewActionMenuProps> = ({
    fhirBaseUrl,
    value = "bienveillance",
    organizationReferences,
    poleOrganization,
    triggerMode = "menu",
    triggerLabel,
    onCreated,
    onOpen
}) => {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [modalMode, setModalMode] = useState<ActionPlanCreationMode>();
    const [organizations, setOrganizations] = useState<OrganizationNode[]>([]);

    const organizationKey = organizationReferences.join("|");
    const poleReference = poleOrganization?.reference;
    const selectableReferences = useMemo(() => {
        const references = [...organizationReferences];
        if (poleReference && !references.includes(poleReference)) {
            references.push(poleReference);
        }
        return references;
    }, [organizationKey, poleReference]);

    const defaultOrganizationReference = organizationReferences.length > 1 && poleReference
        ? poleReference
        : organizationReferences[0] ?? poleReference ?? "";

    const organizationService = useMemo(
        () => fhirBaseUrl ? new OrganizationService({ fhirBaseUrl }) : undefined,
        [fhirBaseUrl]
    );

    useEffect(() => {
        if (!organizationService || selectableReferences.length === 0) {
            setOrganizations([]);
            return;
        }

        let mounted = true;
        Promise.all(selectableReferences.map(async (reference) => {
            if (reference === poleOrganization?.reference) {
                const id = reference.split("/").pop() ?? reference;
                return {
                    id,
                    name: poleOrganization.name,
                    reference,
                    children: []
                } as OrganizationNode;
            }

            const id = reference.split("/").pop() ?? reference;
            try {
                return await organizationService.getOrganizationByIdentifier(id);
            } catch (error) {
                console.error(error);
                return {
                    id,
                    name: id.replace(/-/g, " "),
                    reference,
                    children: []
                } as OrganizationNode;
            }
        })).then((result) => {
            if (mounted) setOrganizations(result);
        });

        return () => {
            mounted = false;
        };
    }, [organizationService, organizationKey, poleReference, poleOrganization?.name]);

    const openMenu = () => {
        setMenuOpen((current) => {
            const next = !current;
            if (next) onOpen?.();
            return next;
        });
    };

    const openModal = (mode: ActionPlanCreationMode) => {
        onOpen?.();
        setMenuOpen(false);
        setModalMode(mode);
    };

    const directLabel = triggerLabel ?? (triggerMode === "need"
        ? "Je déclare un besoin"
        : "Proposer une solution");

    return (
        <>
            <div className={triggerMode === "menu" ? styles.wrapper : styles.directWrapper}>
                {triggerMode === "menu" ? (
                    <>
                        <button
                            type="button"
                            className={styles.trigger}
                            aria-expanded={menuOpen}
                            onClick={openMenu}
                        >
                            <span aria-hidden="true">＋</span>
                            <span>Nouvelle action</span>
                            <span className={styles.chevron} aria-hidden="true">⌄</span>
                        </button>

                        {menuOpen && (
                            <div className={styles.menu}>
                                <button type="button" onClick={() => openModal("need")}>Déclarer un besoin</button>
                                <button type="button" onClick={() => openModal("proposal")}>Proposer une solution</button>
                                <button type="button" onClick={() => navigate("/calendrier")}>Parcourir les ateliers programmés</button>
                            </div>
                        )}
                    </>
                ) : (
                    <button
                        type="button"
                        className={styles.directTrigger}
                        onClick={() => openModal(triggerMode)}
                    >
                        <span aria-hidden="true">＋</span>
                        <span>{directLabel}</span>
                    </button>
                )}
            </div>

            {modalMode && createPortal(
                <ActionModal
                    mode={modalMode}
                    fhirBaseUrl={fhirBaseUrl}
                    initialValue={value}
                    organizations={organizations}
                    organizationReferences={selectableReferences}
                    defaultOrganizationReference={defaultOrganizationReference}
                    poleReference={poleReference}
                    onClose={() => setModalMode(undefined)}
                    onCreated={(request, mode) => {
                        setModalMode(undefined);
                        onCreated?.(request, mode);
                    }}
                />,
                document.body
            )}
        </>
    );
};

interface ActionModalProps {
    mode: ActionPlanCreationMode;
    fhirBaseUrl?: string;
    initialValue: ValueCode;
    organizations: OrganizationNode[];
    organizationReferences: string[];
    defaultOrganizationReference: string;
    poleReference?: string;
    onClose: () => void;
    onCreated: (request: ServiceRequest, mode: ActionPlanCreationMode) => void;
}

const ActionModal: FunctionComponent<ActionModalProps> = ({
    mode,
    fhirBaseUrl,
    initialValue,
    organizations,
    organizationReferences,
    defaultOrganizationReference,
    poleReference,
    onClose,
    onCreated
}) => {
    const [organizationReference, setOrganizationReference] = useState(defaultOrganizationReference);
    const [selectedValue, setSelectedValue] = useState<ValueCode>(initialValue);
    const [selectedTheme, setSelectedTheme] = useState<UpCareThemeCode>(UPCARE_THEMES[0].code);
    const [priority, setPriority] = useState<NeedPriority>("medium");
    const [actionType, setActionType] = useState<ProposalActionType>("atelier");
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string>();

    const creationService = useMemo(
        () => fhirBaseUrl ? new ActionPlanCreationService(fhirBaseUrl) : undefined,
        [fhirBaseUrl]
    );

    useEffect(() => {
        if (!organizationReference && defaultOrganizationReference) {
            setOrganizationReference(defaultOrganizationReference);
        }
    }, [organizationReference, defaultOrganizationReference]);

    const selectedOrganization = organizations.find((item) => item.reference === organizationReference);
    const canSubmit = !!creationService && !!organizationReference && description.trim().length > 0 &&
        (mode === "need" || title.trim().length > 0);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (!creationService || !canSubmit) return;

        setSubmitting(true);
        setError(undefined);

        try {
            const created = mode === "need"
                ? await creationService.createNeed({
                    organizationReference,
                    organizationDisplay: selectedOrganization?.name,
                    value: selectedValue,
                    theme: selectedTheme,
                    priority,
                    description
                })
                : await creationService.createProposal({
                    organizationReference,
                    organizationDisplay: selectedOrganization?.name,
                    value: selectedValue,
                    theme: selectedTheme,
                    actionType,
                    title,
                    description
                });

            onCreated(created, mode);
        } catch (reason) {
            console.error(reason);
            setError(reason instanceof Error && reason.message
                ? reason.message
                : "Impossible d'enregistrer l'action.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.backdrop} onMouseDown={(event) => {
            if (event.target === event.currentTarget && !submitting) onClose();
        }}>
            <form className={`${styles.modal} ${mode === "proposal" ? styles.proposalModal : ""}`} onSubmit={handleSubmit}>
                <button type="button" className={styles.close} onClick={onClose} disabled={submitting}>×</button>

                {mode === "need" ? (
                    <>
                        <h2>Déclarer un besoin</h2>

                        <Field label="Secteur / pôle concerné">
                            <OrganizationSelect
                                value={organizationReference}
                                organizations={organizations}
                                references={organizationReferences}
                                poleReference={poleReference}
                                onChange={setOrganizationReference}
                            />
                        </Field>

                        <ValueSelector value={selectedValue} onChange={setSelectedValue} />
                        <ThemeSelector value={selectedTheme} onChange={setSelectedTheme} />

                        <Field label="Priorité">
                            <div className={styles.pills}>
                                {(["high", "medium", "low"] as NeedPriority[]).map((item) => (
                                    <button
                                        key={item}
                                        type="button"
                                        className={priority === item ? styles.selectedNeutralPill : styles.pill}
                                        onClick={() => setPriority(item)}
                                    >
                                        {item === "high" ? "Haute" : item === "medium" ? "Moyenne" : "Basse"}
                                    </button>
                                ))}
                            </div>
                        </Field>

                        <Field label="Description du besoin">
                            <textarea
                                value={description}
                                onChange={(event) => setDescription(event.target.value)}
                                placeholder="Décrivez la difficulté ou le besoin rencontré..."
                            />
                        </Field>
                    </>
                ) : (
                    <>
                        <div className={styles.proposalTopBadges}>
                            <span>{ACTION_TYPE_LABELS[actionType]}</span>
                            <span className={styles.valuePreview}>{VALUE_LABELS[selectedValue]}</span>
                        </div>
                        <h2>Proposer une action</h2>
                        <p className={styles.subtitle}>Partagez une initiative pour renforcer nos valeurs communes</p>

                        <Field label="Type d'action" upper>
                            <div className={styles.pills}>
                                {(Object.keys(ACTION_TYPE_LABELS) as ProposalActionType[]).map((item) => (
                                    <button
                                        key={item}
                                        type="button"
                                        className={actionType === item ? styles.selectedActionPill : styles.pill}
                                        onClick={() => setActionType(item)}
                                    >
                                        {ACTION_TYPE_LABELS[item]}
                                    </button>
                                ))}
                            </div>
                        </Field>

                        <Field label="Titre" upper>
                            <input
                                value={title}
                                onChange={(event) => setTitle(event.target.value)}
                                placeholder="Titre de la proposition..."
                            />
                        </Field>

                        <ValueSelector value={selectedValue} onChange={setSelectedValue} upper />
                        <ThemeSelector value={selectedTheme} onChange={setSelectedTheme} upper />

                        <Field label="Secteur / pôle concerné" upper>
                            <OrganizationSelect
                                value={organizationReference}
                                organizations={organizations}
                                references={organizationReferences}
                                poleReference={poleReference}
                                onChange={setOrganizationReference}
                            />
                        </Field>

                        <Field label="Description / objectif" upper>
                            <textarea
                                value={description}
                                onChange={(event) => setDescription(event.target.value)}
                                placeholder="Décrivez l'action proposée, ses objectifs et son déroulé..."
                            />
                        </Field>
                    </>
                )}

                {error && <p className={styles.error}>{error}</p>}

                <button type="submit" className={styles.submit} disabled={!canSubmit || submitting}>
                    {submitting
                        ? "Enregistrement..."
                        : mode === "need"
                            ? "Transmettre la demande"
                            : "Soumettre la proposition"}
                </button>
            </form>
        </div>
    );
};

const ThemeSelector: FunctionComponent<{
    value: UpCareThemeCode;
    onChange: (value: UpCareThemeCode) => void;
    upper?: boolean;
}> = ({ value, onChange, upper }) => (
    <Field label="Thématique" upper={upper}>
        <div className={styles.pills}>
            {UPCARE_THEMES.map((theme) => (
                <button
                    key={theme.code}
                    type="button"
                    className={value === theme.code ? styles.selectedThemePill : styles.pill}
                    onClick={() => onChange(theme.code)}
                >
                    {theme.display}
                </button>
            ))}
        </div>
    </Field>
);

const OrganizationSelect: FunctionComponent<{
    value: string;
    organizations: OrganizationNode[];
    references: string[];
    poleReference?: string;
    onChange: (value: string) => void;
}> = ({ value, organizations, references, poleReference, onChange }) => {
    const sortedReferences = [
        ...references.filter((reference) => reference === poleReference),
        ...references.filter((reference) => reference !== poleReference)
    ];

    return (
        <select value={value} onChange={(event) => onChange(event.target.value)}>
            {sortedReferences.map((reference) => {
                const organization = organizations.find((item) => item.reference === reference);
                const name = organization?.name ?? reference.split("/").pop()?.replace(/-/g, " ") ?? reference;
                return (
                    <option key={reference} value={reference}>
                        {reference === poleReference ? `Pôle — ${name}` : name}
                    </option>
                );
            })}
        </select>
    );
};

const ValueSelector: FunctionComponent<{
    value: ValueCode;
    onChange: (value: ValueCode) => void;
    upper?: boolean;
}> = ({ value, onChange, upper }) => (
    <Field label="Valeur concernée" upper={upper}>
        <div className={styles.pills}>
            {VALUE_CODES.map((item) => (
                <button
                    key={item}
                    type="button"
                    data-value={item}
                    className={`${styles.valuePill} ${value === item ? styles.selectedValuePill : ""}`}
                    onClick={() => onChange(item)}
                >
                    {VALUE_LABELS[item]}
                </button>
            ))}
        </div>
    </Field>
);

const Field: FunctionComponent<{
    label: string;
    upper?: boolean;
    children: React.ReactNode;
}> = ({ label, upper, children }) => (
    <label className={styles.field}>
        <span className={upper ? styles.upperLabel : ""}>{label}</span>
        {children}
    </label>
);

const ACTION_TYPE_LABELS: Record<ProposalActionType, string> = {
    atelier: "Atelier",
    formation: "Formation",
    "bonne-pratique": "Bonne pratique"
};

export default ActionPlanNewActionMenu;
